// src/models/Service.js - Service Model with Validation

export class Service {
  constructor(data = {}) {
    this.id = data.id || null;
    this.businessId = data.businessId || null;
    this.name = data.name || '';
    this.description = data.description || '';
    this.category = data.category || 'general';
    this.duration = data.duration || 30; // minutes
    this.price = data.price || 0; // dollars
    this.active = data.active !== undefined ? data.active : true;
    
    // Advanced Settings
    this.requiresDeposit = data.requiresDeposit || false;
    this.depositAmount = data.depositAmount || 0;
    this.depositType = data.depositType || 'fixed'; // 'fixed' or 'percentage'
    this.onlineBookingEnabled = data.onlineBookingEnabled !== undefined ? data.onlineBookingEnabled : true;
    this.maxAdvanceBookingDays = data.maxAdvanceBookingDays || 30;
    this.minAdvanceBookingHours = data.minAdvanceBookingHours || 2;
    
    // Employee Assignment
    this.assignedEmployees = data.assignedEmployees || []; // Array of employee IDs
    this.allowAnyEmployee = data.allowAnyEmployee !== undefined ? data.allowAnyEmployee : true;
    
    // Metadata
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
    this.sortOrder = data.sortOrder || 0;
  }

  // Validation Methods
  validate() {
    const errors = [];

    // Required fields
    if (!this.name || this.name.trim().length < 2) {
      errors.push('Service name must be at least 2 characters');
    }

    if (!this.businessId) {
      errors.push('Business ID is required');
    }

    // Duration validation
    if (!Number.isInteger(this.duration) || this.duration < 5 || this.duration > 480) {
      errors.push('Duration must be between 5 and 480 minutes');
    }

    // Price validation
    if (this.price < 0 || this.price > 10000) {
      errors.push('Price must be between $0 and $10,000');
    }

    if (!Number.isFinite(this.price)) {
      errors.push('Price must be a valid number');
    }

    // Category validation
    const validCategories = [
      'haircut', 'coloring', 'styling', 'treatment', 'beard', 'nails',
      'massage', 'facial', 'waxing', 'makeup', 'consultation', 'general'
    ];
    if (!validCategories.includes(this.category)) {
      errors.push('Invalid service category');
    }

    // Deposit validation
    if (this.requiresDeposit) {
      if (this.depositType === 'fixed') {
        if (this.depositAmount <= 0 || this.depositAmount > this.price) {
          errors.push('Fixed deposit amount must be greater than 0 and not exceed service price');
        }
      } else if (this.depositType === 'percentage') {
        if (this.depositAmount <= 0 || this.depositAmount > 100) {
          errors.push('Percentage deposit must be between 1 and 100');
        }
      } else {
        errors.push('Deposit type must be either "fixed" or "percentage"');
      }
    }

    // Booking timing validation
    if (this.maxAdvanceBookingDays < 1 || this.maxAdvanceBookingDays > 365) {
      errors.push('Max advance booking days must be between 1 and 365');
    }

    if (this.minAdvanceBookingHours < 0 || this.minAdvanceBookingHours > 168) {
      errors.push('Min advance booking hours must be between 0 and 168 (1 week)');
    }

    // Employee assignment validation
    if (!this.allowAnyEmployee && this.assignedEmployees.length === 0) {
      errors.push('Service must be assigned to at least one employee or allow any employee');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Business Logic Methods
  getCalculatedDepositAmount() {
    if (!this.requiresDeposit) return 0;
    
    if (this.depositType === 'fixed') {
      return this.depositAmount;
    } else if (this.depositType === 'percentage') {
      return Math.round((this.price * this.depositAmount) / 100 * 100) / 100; // Round to 2 decimal places
    }
    
    return 0;
  }

  getRemainingAmount() {
    return this.price - this.getCalculatedDepositAmount();
  }

  canBeBookedBy(employeeId) {
    if (this.allowAnyEmployee) return true;
    return this.assignedEmployees.includes(employeeId);
  }

  canBeBookedAt(requestedDate) {
    const now = new Date();
    const requestDate = new Date(requestedDate);
    
    // Check minimum advance booking time
    const minAdvanceMs = this.minAdvanceBookingHours * 60 * 60 * 1000;
    if (requestDate.getTime() - now.getTime() < minAdvanceMs) {
      return {
        canBook: false,
        reason: `Service requires at least ${this.minAdvanceBookingHours} hours advance booking`
      };
    }
    
    // Check maximum advance booking time
    const maxAdvanceMs = this.maxAdvanceBookingDays * 24 * 60 * 60 * 1000;
    if (requestDate.getTime() - now.getTime() > maxAdvanceMs) {
      return {
        canBook: false,
        reason: `Service can only be booked up to ${this.maxAdvanceBookingDays} days in advance`
      };
    }
    
    return { canBook: true, reason: null };
  }

  getDisplayDuration() {
    if (this.duration < 60) {
      return `${this.duration} min`;
    }
    
    const hours = Math.floor(this.duration / 60);
    const minutes = this.duration % 60;
    
    if (minutes === 0) {
      return `${hours} hr${hours > 1 ? 's' : ''}`;
    }
    
    return `${hours} hr${hours > 1 ? 's' : ''} ${minutes} min`;
  }

  getDisplayPrice() {
    if (this.price === 0) return 'Free';
    return `$${this.price.toFixed(2)}`;
  }

  isPremiumService() {
    return this.price > 100 || this.duration > 120;
  }

  // Update Methods
  updateBasicInfo(updates) {
    const allowedFields = [
      'name', 'description', 'category', 'duration', 'price', 
      'active', 'onlineBookingEnabled', 'sortOrder'
    ];
    
    allowedFields.forEach(field => {
      if (updates.hasOwnProperty(field)) {
        this[field] = updates[field];
      }
    });

    this.updatedAt = new Date().toISOString();
    return this.validate();
  }

  updateDepositSettings(depositSettings) {
    const allowedFields = [
      'requiresDeposit', 'depositAmount', 'depositType'
    ];
    
    allowedFields.forEach(field => {
      if (depositSettings.hasOwnProperty(field)) {
        this[field] = depositSettings[field];
      }
    });

    this.updatedAt = new Date().toISOString();
    return this.validate();
  }

  updateBookingSettings(bookingSettings) {
    const allowedFields = [
      'maxAdvanceBookingDays', 'minAdvanceBookingHours', 
      'onlineBookingEnabled'
    ];
    
    allowedFields.forEach(field => {
      if (bookingSettings.hasOwnProperty(field)) {
        this[field] = bookingSettings[field];
      }
    });

    this.updatedAt = new Date().toISOString();
    return this.validate();
  }

  updateEmployeeAssignment(employeeSettings) {
    if (employeeSettings.hasOwnProperty('allowAnyEmployee')) {
      this.allowAnyEmployee = employeeSettings.allowAnyEmployee;
    }
    
    if (employeeSettings.hasOwnProperty('assignedEmployees')) {
      if (!Array.isArray(employeeSettings.assignedEmployees)) {
        return {
          isValid: false,
          errors: ['Assigned employees must be an array']
        };
      }
      this.assignedEmployees = employeeSettings.assignedEmployees;
    }

    this.updatedAt = new Date().toISOString();
    return this.validate();
  }

  // Data Transformation Methods
  toFirebaseData() {
    return {
      name: this.name,
      description: this.description,
      category: this.category,
      duration: this.duration,
      price: this.price,
      active: this.active,
      requires_deposit: this.requiresDeposit,
      deposit_amount: this.depositAmount,
      deposit_type: this.depositType,
      online_booking_enabled: this.onlineBookingEnabled,
      max_advance_booking_days: this.maxAdvanceBookingDays,
      min_advance_booking_hours: this.minAdvanceBookingHours,
      assigned_employees: this.assignedEmployees,
      allow_any_employee: this.allowAnyEmployee,
      created_at: this.createdAt,
      updated_at: this.updatedAt,
      sort_order: this.sortOrder
    };
  }

  toPublicData() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      category: this.category,
      duration: this.duration,
      displayDuration: this.getDisplayDuration(),
      price: this.price,
      displayPrice: this.getDisplayPrice(),
      requiresDeposit: this.requiresDeposit,
      depositAmount: this.getCalculatedDepositAmount(),
      onlineBookingEnabled: this.onlineBookingEnabled,
      isPremium: this.isPremiumService()
    };
  }

  toBookingData() {
    return {
      id: this.id,
      name: this.name,
      duration: this.duration,
      price: this.price,
      depositRequired: this.requiresDeposit,
      depositAmount: this.getCalculatedDepositAmount(),
      remainingAmount: this.getRemainingAmount(),
      category: this.category
    };
  }

  // Static Factory Methods
  static fromServiceTemplate(template, businessId) {
    return new Service({
      businessId,
      name: template.name,
      description: template.description || '',
      category: template.category || 'general',
      duration: parseInt(template.duration) || 30,
      price: parseFloat(template.price) || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sortOrder: template.sortOrder || 0
    });
  }

  static fromRegistrationService(serviceKey, serviceData, businessId) {
    const serviceNames = {
      haircut: 'Haircut',
      beard_trim: 'Beard Trim',
      full_service: 'Full Service',
      consultation: 'Consultation',
      color: 'Hair Color',
      styling: 'Hair Styling',
      manicure: 'Manicure',
      pedicure: 'Pedicure',
      gel_polish: 'Gel Polish',
      massage: 'Massage',
      facial: 'Facial',
      body_treatment: 'Body Treatment'
    };

    const serviceCategories = {
      haircut: 'haircut',
      beard_trim: 'beard',
      full_service: 'haircut',
      consultation: 'consultation',
      color: 'coloring',
      styling: 'styling',
      manicure: 'nails',
      pedicure: 'nails',
      gel_polish: 'nails',
      massage: 'massage',
      facial: 'facial',
      body_treatment: 'treatment'
    };

    return new Service({
      id: `${businessId}_${serviceKey}`,
      businessId,
      name: serviceNames[serviceKey] || serviceKey.replace('_', ' '),
      category: serviceCategories[serviceKey] || 'general',
      duration: parseInt(serviceData.duration) || 30,
      price: parseFloat(serviceData.price) || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  }

  static fromFirebaseData(id, businessId, firebaseData) {
    return new Service({
      id,
      businessId,
      name: firebaseData.name,
      description: firebaseData.description,
      category: firebaseData.category,
      duration: firebaseData.duration,
      price: firebaseData.price,
      active: firebaseData.active,
      requiresDeposit: firebaseData.requires_deposit,
      depositAmount: firebaseData.deposit_amount,
      depositType: firebaseData.deposit_type,
      onlineBookingEnabled: firebaseData.online_booking_enabled,
      maxAdvanceBookingDays: firebaseData.max_advance_booking_days,
      minAdvanceBookingHours: firebaseData.min_advance_booking_hours,
      assignedEmployees: firebaseData.assigned_employees,
      allowAnyEmployee: firebaseData.allow_any_employee,
      createdAt: firebaseData.created_at,
      updatedAt: firebaseData.updated_at,
      sortOrder: firebaseData.sort_order
    });
  }

  // Static Helper Methods
  static getDefaultServiceTemplates(businessType) {
    const templates = {
      barbershop: [
        { name: 'Haircut', duration: '30', price: '25', category: 'haircut' },
        { name: 'Beard Trim', duration: '20', price: '15', category: 'beard' },
        { name: 'Full Service', duration: '60', price: '40', category: 'haircut' },
        { name: 'Consultation', duration: '15', price: '0', category: 'consultation' }
      ],
      hair_salon: [
        { name: 'Haircut', duration: '45', price: '65', category: 'haircut' },
        { name: 'Hair Color', duration: '120', price: '150', category: 'coloring' },
        { name: 'Hair Styling', duration: '30', price: '45', category: 'styling' },
        { name: 'Consultation', duration: '15', price: '0', category: 'consultation' }
      ],
      nail_salon: [
        { name: 'Manicure', duration: '45', price: '35', category: 'nails' },
        { name: 'Pedicure', duration: '60', price: '45', category: 'nails' },
        { name: 'Gel Polish', duration: '30', price: '25', category: 'nails' },
        { name: 'Consultation', duration: '15', price: '0', category: 'consultation' }
      ],
      spa: [
        { name: 'Massage', duration: '60', price: '80', category: 'massage' },
        { name: 'Facial', duration: '75', price: '95', category: 'facial' },
        { name: 'Body Treatment', duration: '90', price: '120', category: 'treatment' },
        { name: 'Consultation', duration: '15', price: '0', category: 'consultation' }
      ]
    };

    return templates[businessType] || templates.barbershop;
  }

  // Enhanced Customization Methods
  static getAllServiceCategories() {
    return [
      { id: 'haircut', name: 'Haircuts & Styling', icon: '✂️' },
      { id: 'coloring', name: 'Hair Coloring', icon: '🎨' },
      { id: 'styling', name: 'Hair Styling', icon: '💇‍♀️' },
      { id: 'treatment', name: 'Hair Treatments', icon: '🧴' },
      { id: 'beard', name: 'Beard Services', icon: '🧔' },
      { id: 'nails', name: 'Nail Services', icon: '💅' },
      { id: 'massage', name: 'Massage Therapy', icon: '💆‍♀️' },
      { id: 'facial', name: 'Facial Treatments', icon: '😌' },
      { id: 'waxing', name: 'Waxing Services', icon: '🕯️' },
      { id: 'makeup', name: 'Makeup Services', icon: '💄' },
      { id: 'consultation', name: 'Consultations', icon: '💬' },
      { id: 'general', name: 'General Services', icon: '🔧' }
    ];
  }

  static getPopularServicesByCategory(category) {
    const popularServices = {
      haircut: [
        'Women\'s Cut', 'Men\'s Cut', 'Kids Cut', 'Trim', 'Layered Cut', 'Bob Cut', 'Pixie Cut'
      ],
      coloring: [
        'Full Color', 'Root Touch-up', 'Highlights', 'Lowlights', 'Balayage', 'Ombre', 'Color Correction'
      ],
      styling: [
        'Blowout', 'Updo', 'Curls', 'Straightening', 'Braiding', 'Wedding Style', 'Special Event Style'
      ],
      treatment: [
        'Deep Conditioning', 'Keratin Treatment', 'Hair Mask', 'Scalp Treatment', 'Protein Treatment'
      ],
      beard: [
        'Beard Trim', 'Mustache Trim', 'Beard Shape', 'Hot Towel Shave', 'Straight Razor Shave'
      ],
      nails: [
        'Basic Manicure', 'Gel Manicure', 'French Manicure', 'Pedicure', 'Nail Art', 'Acrylic Nails'
      ],
      massage: [
        'Swedish Massage', 'Deep Tissue', 'Hot Stone', 'Prenatal Massage', 'Sports Massage'
      ],
      facial: [
        'Classic Facial', 'Anti-Aging Facial', 'Acne Treatment', 'Hydrating Facial', 'Chemical Peel'
      ],
      waxing: [
        'Eyebrow Wax', 'Lip Wax', 'Leg Wax', 'Bikini Wax', 'Full Body Wax'
      ],
      makeup: [
        'Bridal Makeup', 'Special Event', 'Everyday Look', 'Makeup Lesson', 'Lash Extensions'
      ]
    };
    
    return popularServices[category] || [];
  }

  // Create custom service from scratch
  static createCustomService(businessId, customData) {
    return new Service({
      id: `${businessId}_custom_${Date.now()}`,
      businessId,
      name: customData.name,
      description: customData.description || '',
      category: customData.category || 'general',
      duration: parseInt(customData.duration) || 30,
      price: parseFloat(customData.price) || 0,
      requiresDeposit: customData.requiresDeposit || false,
      depositAmount: parseFloat(customData.depositAmount) || 0,
      depositType: customData.depositType || 'fixed',
      onlineBookingEnabled: customData.onlineBookingEnabled !== false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sortOrder: customData.sortOrder || 999 // Custom services appear last by default
    });
  }

  // Service suggestions based on business type and existing services
  static getSuggestedServices(businessType, existingServices = []) {
    const allTemplates = this.getDefaultServiceTemplates(businessType);
    const existingNames = existingServices.map(s => s.name.toLowerCase());
    
    return allTemplates.filter(template => 
      !existingNames.includes(template.name.toLowerCase())
    );
  }
}